import axios from 'axios';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return 'https://pravarainfotech.in/api';
    }
  }
  return envUrl || 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL.replace(/\/api\/?$/, ''),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token and prefix relative paths with /api
api.interceptors.request.use(
  (config) => {
    // Prefix relative paths with /api
    if (config.url && config.url.startsWith('/') && !config.url.startsWith('/api')) {
      config.url = `/api${config.url}`;
    }
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      if (reqUrl.includes('/admin')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (username, password) =>
    api.post('/admin/login', { username, password }),
};

// Contact API calls
export const contactAPI = {
  getAll: () => api.get('/contact'),
  getById: (id) => api.get(`/contact/${id}`),
  markAsRead: (id) => api.put(`/admin/submissions/${id}/read`),
  delete: (id) => api.delete(`/admin/submissions/${id}`),
};

// Newsletter API calls
export const newsletterAPI = {
  getSubscribers: (skip = 0, limit = 10) =>
    api.get('/newsletter/subscribers', { params: { skip, limit } }),
  subscribe: (email) =>
    api.post('/newsletter/subscribe', { email }),
  unsubscribe: (email) =>
    api.post('/newsletter/unsubscribe', { email }),
};

// Job Applications API calls
export const applicationsAPI = {
  getAll: (skip = 0, limit = 10) =>
    api.get('/applications', { params: { skip, limit } }),
  getById: (id) => api.get(`/applications/${id}`),
  delete: (id) => api.delete(`/applications/${id}`),
};

// Internship Applications API calls
export const internshipsAPI = {
  submit: (formData) => api.post('/internships', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAll: (params) => api.get('/admin/internships', { params }),
  getById: (id) => api.get(`/admin/internships/${id}`),
  updateStatus: (id, status) => api.put(`/admin/internships/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/internships/${id}`),
};

// Custom Software Project Enquiries API calls
export const customSoftwareEnquiriesAPI = {
  submit: (formData) => api.post('/software-project-enquiries', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAll: (params) => api.get('/admin/software-project-enquiries', { params }),
  getById: (id) => api.get(`/admin/software-project-enquiries/${id}`),
  updateStatus: (id, status) => api.put(`/admin/software-project-enquiries/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/software-project-enquiries/${id}`),
};

// Custom MBA Project Enquiries API calls
export const customMbaEnquiriesAPI = {
  submit: (formData) => api.post('/mba-project-enquiries', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAll: (params) => api.get('/admin/mba-project-enquiries', { params }),
  getById: (id) => api.get(`/admin/mba-project-enquiries/${id}`),
  updateStatus: (id, status) => api.put(`/admin/mba-project-enquiries/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/mba-project-enquiries/${id}`),
};

// Projects API calls
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getPublic: (params = {}) => api.get('/projects/public', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  toggleStatus: (id) => api.put(`/projects/${id}/toggle-status`),
  delete: (id) => api.delete(`/projects/${id}`),
  bulkPriceUpdate: (data) => api.post('/admin/projects/bulk-price-update', data),
};

// Collaborations API calls
export const collaborationsAPI = {
  getAll: () => api.get('/collaborations'),
  getById: (id) => api.get(`/collaborations/${id}`),
  create: (data) => api.post('/collaborations', data),
  update: (id, data) => api.put(`/collaborations/${id}`, data),
  delete: (id) => api.delete(`/collaborations/${id}`),
};

// Analytics & Payment API calls
export const analyticsAPI = {
  getSummary: () => api.get('/analytics/summary'),
};


// Enquiries API calls
export const enquiriesAPI = {
  create: (data) => api.post('/enquiries', data),
  getAll: () => api.get('/admin/enquiries'),
  updateStatus: (id, status) => api.put(`/admin/enquiries/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/enquiries/${id}`),

  // MBA CRM Endpoints
  getMbaEnquiries: (search) => api.get('/admin/mba-enquiries', { params: { search } }),
  getMbaEnquiryById: (id) => api.get(`/admin/mba-enquiries/${id}`),
  updateMbaStatus: (id, status) => api.put(`/admin/mba-enquiries/${id}/status`, { status }),
  sendMbaEmail: (id, formData) => api.post(`/admin/mba-enquiries/${id}/send-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMbaEmailHistory: (id) => api.get(`/admin/mba-enquiries/${id}/email-history`),
  getCompletedMba: () => api.get('/admin/completed-mba-enquiries'),

  // Software CRM Endpoints
  getSoftwareEnquiries: (search) => api.get('/admin/software-enquiries', { params: { search } }),
  getSoftwareEnquiryById: (id) => api.get(`/admin/software-enquiries/${id}`),
  updateSoftwareStatus: (id, status) => api.put(`/admin/software-enquiries/${id}/status`, { status }),
  sendSoftwareEmail: (id, formData) => api.post(`/admin/software-enquiries/${id}/send-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getSoftwareEmailHistory: (id) => api.get(`/admin/software-enquiries/${id}/email-history`),
  getCompletedSoftware: () => api.get('/admin/completed-software-enquiries'),
  // Shared Archive Endpoint (Soft Delete)
  archive: (id) => api.put(`/admin/enquiries/${id}/archive`),

  // Payment CRM Endpoints
  updatePaymentStatus: (id, status) => api.put(`/admin/enquiries/${id}/payment-status`, { payment_status: status }),
  uploadPaymentProof: (id, formData) => api.post(`/admin/enquiries/${id}/upload-proof`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadPaymentProof: (id) => api.get(`/admin/enquiries/${id}/proof`, { responseType: 'blob' }),
  resendPaymentEmail: (id) => api.post(`/admin/enquiries/${id}/resend-payment-email`),
};

// MBA Projects API calls
export const mbaAPI = {
  getProjects: (params = {}) => api.get('/mba-projects', { params }),
  getProjectById: (id) => api.get(`/mba-projects/${id}`),
  getDepartments: () => api.get('/mba-departments'),
  getTypes: () => api.get('/mba-project-types'),
  createDepartment: (data) => api.post('/admin/mba-departments', data),
  createType: (data) => api.post('/admin/mba-project-types', data),
  createProject: (data) => api.post('/admin/mba-projects', data),
  updateProject: (id, data) => api.put(`/admin/mba-projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/mba-projects/${id}`),
  uploadFile: (formData) => api.post('/admin/mba-projects/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkPriceUpdate: (data) => api.post('/admin/mba-projects/bulk-price-update', data),
  // University endpoints
  getUniversities: () => api.get('/mba-universities'),
  getUniversityBySlug: (slug) => api.get(`/mba-universities/${slug}`),
  adminGetUniversities: () => api.get('/admin/mba-universities'),
  adminCreateUniversity: (data) => api.post('/admin/mba-universities', data),
  adminUpdateUniversity: (id, data) => api.put(`/admin/mba-universities/${id}`, data),
  adminDeleteUniversity: (id) => api.delete(`/admin/mba-universities/${id}`),
  adminUploadUniversityLogo: (id, formData) =>
    api.post(`/admin/mba-universities/${id}/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminUploadUniversityDoc: (id, formData) =>
    api.post(`/admin/mba-universities/${id}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  adminUploadUniversitySampleReport: (id, formData) =>
    api.post(`/admin/mba-universities/${id}/upload-sample-report`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};


// CMS API calls
export const cmsAPI = {
  // Services
  getServices: () => api.get('/cms/services'),
  createService: (formData) => api.post('/cms/services', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateService: (id, formData) => api.put(`/cms/services/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteService: (id) => api.delete(`/cms/services/${id}`),

  // Home
  getHome: () => api.get('/cms/home'),
  updateHome: (key, formData) => api.put(`/cms/home/${key}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Team
  getTeam: () => api.get('/cms/team'),
  createTeam: (formData) => api.post('/cms/team', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateTeam: (id, formData) => api.put(`/cms/team/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTeam: (id) => api.delete(`/cms/team/${id}`),

  // Gallery
  getGallery: () => api.get('/cms/gallery'),
  addGalleryItem: (formData) => api.post('/cms/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteGalleryItem: (id) => api.delete(`/cms/gallery/${id}`),

  // Social Links
  getSocialLinks: () => api.get('/cms/social-links'),
  createSocialLink: (formData) => api.post('/cms/social-links', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateSocialLink: (id, formData) => api.put(`/cms/social-links/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteSocialLink: (id) => api.delete(`/cms/social-links/${id}`),

  // Site Contact
  getContactInfo: () => api.get('/cms/contact-info'),
  updateContactInfo: (formData) => api.put('/cms/contact-info', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Testimonials
  getTestimonials: () => api.get('/cms/testimonials'),

  // Company Partners
  getCompanyPartners: () => api.get('/cms/company-partners'),
  createCompanyPartner: (formData) => api.post('/cms/company-partners', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCompanyPartner: (id, formData) => api.put(`/cms/company-partners/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCompanyPartner: (id) => api.delete(`/cms/company-partners/${id}`),

  // CSR Partners
  getCsrPartners: () => api.get('/cms/csr-partners'),
  createCsrPartner: (formData) => api.post('/cms/csr-partners', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCsrPartner: (id, formData) => api.put(`/cms/csr-partners/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCsrPartner: (id) => api.delete(`/cms/csr-partners/${id}`),

  // Collaboration Projects
  getCollaborationProjects: () => api.get('/cms/collaboration-projects'),
  createCollaborationProject: (formData) => api.post('/cms/collaboration-projects', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCollaborationProject: (id, formData) => api.put(`/cms/collaboration-projects/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCollaborationProject: (id) => api.delete(`/cms/collaboration-projects/${id}`),

  // Collaboration Features
  getCollaborationFeatures: () => api.get('/cms/collaboration-features'),
  createCollaborationFeature: (formData) => api.post('/cms/collaboration-features', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCollaborationFeature: (id, formData) => api.put(`/cms/collaboration-features/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCollaborationFeature: (id) => api.delete(`/cms/collaboration-features/${id}`),
};

export default api;

export const getImageUrl = (url, fallback = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400"><rect width="100%" height="100%" fill="%230f172a"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%2338bdf8" font-weight="bold">No Image Available</text></svg>') => {
  if (!url) return fallback;
  if (url.startsWith('http')) return url;
  
  const rawApiUrl = API_BASE_URL;
  const hasApiInUrl = rawApiUrl.includes('/api');
  const apiBaseWithoutApi = rawApiUrl.replace(/\/api\/?$/, '');
  
  const uploadsIndex = url.indexOf('uploads/');
  if (uploadsIndex !== -1) {
    const cleanUrl = '/' + url.substring(uploadsIndex);
    if (hasApiInUrl) {
      return `${apiBaseWithoutApi}/api${cleanUrl}`;
    } else {
      return `${apiBaseWithoutApi}${cleanUrl}`;
    }
  }
  
  if (url.startsWith('/images/')) return url;
  
  if (url.startsWith('/api/') || url.startsWith('api/')) {
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${apiBaseWithoutApi}${cleanUrl}`;
  }
  
  return url;
};
